/**
 * OpenWebUI client for the server-side-tool flow (verified 03.09.2026 against
 * oi.destination.one, OpenWebUI 0.10.2):
 *
 *   1. POST /api/v1/chats/new  — the chat WITH the assistant message already in
 *      the history as an empty placeholder (done:false). Without the placeholder
 *      the completion returns 200 in 0.2 s and nothing ever runs.
 *   2. POST /api/chat/completions — chat_id + session_id + id (= placeholder id)
 *      + tool_ids. Returns only { task_ids }; the work happens in the background.
 *   3. GET /api/v1/chats/{id} until history.messages[id].done === true. The
 *      answer text is output[-1] (type message → content[].text); every tool call
 *      and its result sit in output[] as function_call / function_call_output.
 *   4. optionally DELETE /api/v1/chats/{id}.
 */
import type { AssistantAnswer, ErrorKind, ToolCall, ToolResult, WidgetConfig } from './model';

/** A question that went wrong on its way through OpenWebUI. `kind` is what the visitor is told
 *  (chat.ts::errorText), `detail` — the message — what a developer needs: path, status, body. */
export class ServiceError extends Error {
  constructor(
    readonly kind: Exclude<ErrorKind, 'setup'>,
    readonly detail: string,
  ) {
    super(detail);
    this.name = 'ServiceError';
  }
}

const uuid = (): string =>
  (globalThis.crypto && 'randomUUID' in globalThis.crypto)
    ? globalThis.crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
      });

export interface CompletionHandle {
  chatId: string;
  assistantId: string;
}

export class OpenWebUIClient {
  /** The config is read on every call so attributes set after mount still count. */
  constructor(private readonly config: () => WidgetConfig) {}

  private get cfg(): WidgetConfig {
    return this.config();
  }

  private headers(): HeadersInit {
    return { Authorization: `Bearer ${this.cfg.apiKey}`, 'Content-Type': 'application/json' };
  }

  private async json<T>(path: string, init: RequestInit = {}): Promise<T> {
    let res: Response;
    try {
      res = await fetch(this.cfg.baseUrl.replace(/\/$/, '') + path, { ...init, headers: { ...this.headers(), ...(init.headers ?? {}) } });
    } catch (e) {
      // fetch rejects only when no answer arrived at all: offline, DNS, refused connection, CORS
      throw new ServiceError('network', `${path}: ${e instanceof Error ? e.message : String(e)}`);
    }
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new ServiceError(kindOfStatus(res.status), `HTTP ${res.status} on ${path}: ${body.slice(0, 200)}`);
    }
    try {
      return (await res.json()) as T;
    } catch (e) {
      throw new ServiceError('unexpected', `${path}: answer is no JSON (${e instanceof Error ? e.message : String(e)})`);
    }
  }

  /** Start one question. Each widget question is its own OpenWebUI chat — the
   *  history the model sees is passed in `messages`, so the conversation still
   *  has memory without the chat object growing across turns. */
  async ask(history: { role: 'user' | 'assistant'; content: string }[], question: string): Promise<CompletionHandle> {
    const now = Math.floor(Date.now() / 1000);
    const userId = uuid();
    const assistantId = uuid();
    const sessionId = uuid();
    const content = this.cfg.extendedHint ? `${question}\n\n(Hinweis: bei show_connections und show_departures bitte extended=true verwenden; bei Fragen nach einer Linie oder ihrem Verlauf bitte line_course verwenden.)` : question;
    const userMsg = { id: userId, parentId: null, childrenIds: [assistantId], role: 'user', content, timestamp: now, models: [this.cfg.model] };
    const assistantMsg = { id: assistantId, parentId: userId, childrenIds: [], role: 'assistant', content: '', model: this.cfg.model, timestamp: now, done: false };
    const created = await this.json<{ id: string }>('/api/v1/chats/new', {
      method: 'POST',
      body: JSON.stringify({
        chat: {
          title: 'nds-chat-map',
          models: [this.cfg.model],
          messages: [userMsg, assistantMsg],
          history: { currentId: assistantId, messages: { [userId]: userMsg, [assistantId]: assistantMsg } },
        },
      }),
    });
    await this.json('/api/chat/completions', {
      method: 'POST',
      body: JSON.stringify({
        model: this.cfg.model,
        messages: [...history, { role: 'user', content }],
        stream: true,
        chat_id: created.id,
        session_id: sessionId,
        id: assistantId,
        tool_ids: this.cfg.toolIds,
        background_tasks: { title_generation: false, tags_generation: false, follow_up_generation: false },
      }),
    });
    return { chatId: created.id, assistantId };
  }

  /** One poll. Returns the answer as far as it exists. */
  async peek(h: CompletionHandle): Promise<AssistantAnswer> {
    const chat = await this.json<{ chat: { history: { messages: Record<string, { done?: boolean; content?: string; output?: unknown[] }> } } }>(
      `/api/v1/chats/${h.chatId}`,
    );
    const msg = chat.chat?.history?.messages?.[h.assistantId];
    return normalise(msg?.output ?? [], msg?.content ?? '', msg?.done === true);
  }

  async waitFor(h: CompletionHandle, onProgress?: (a: AssistantAnswer) => void, signal?: AbortSignal): Promise<AssistantAnswer> {
    // Not a total budget: a complex question makes many tool calls and can run well past a fixed limit
    // while still making progress — the old fixed `timeoutMs` cut those off mid-answer. Instead give up
    // only after `timeoutMs` of NO change (a genuine stall — the model died or hangs), and keep a hard
    // ceiling as a backstop so a dribble never waits forever. A growing answer never times out.
    const hardCeilingMs = this.cfg.timeoutMs * 4;
    const startedAt = Date.now();
    let lastProgressAt = startedAt;
    let lastSignature = -1;
    for (;;) {
      if (signal?.aborted) throw new Error('abgebrochen');
      const a = await this.peek(h);
      onProgress?.(a);
      if (a.done) return a;
      const signature = a.calls.length + a.results.length + a.running.length + a.text.length;
      if (signature !== lastSignature) { lastSignature = signature; lastProgressAt = Date.now(); }
      const now = Date.now();
      if (now - lastProgressAt > this.cfg.timeoutMs || now - startedAt > hardCeilingMs) {
        throw new ServiceError('timeout', now - startedAt > hardCeilingMs ? `no answer after ${hardCeilingMs / 1000} s` : `no change for ${this.cfg.timeoutMs / 1000} s`);
      }
      await new Promise((r) => setTimeout(r, this.cfg.pollMs));
    }
  }

  async delete(h: CompletionHandle): Promise<void> {
    try {
      await this.json(`/api/v1/chats/${h.chatId}`, { method: 'DELETE' });
    } catch {
      /* best effort */
    }
  }
}

/** What an HTTP error status means for the visitor: the key or the path is not accepted (asking again
 *  cannot help), too many requests, the server failed — or the request itself was refused. */
function kindOfStatus(status: number): ServiceError['kind'] {
  if (status === 401 || status === 403 || status === 404) return 'unavailable';
  if (status === 429) return 'busy';
  return status >= 500 ? 'server' : 'refused';
}

/** Turn OpenWebUI's `output[]` into calls, results and the final text. */
export function normalise(output: unknown[], fallbackText: string, done: boolean): AssistantAnswer {
  const calls: ToolCall[] = [];
  const results: ToolResult[] = [];
  const byCall = new Map<string, string>();
  let text = '';
  for (const raw of output) {
    const o = raw as Record<string, unknown>;
    if (o.type === 'function_call') {
      let args: Record<string, unknown> = {};
      try {
        args = typeof o.arguments === 'string' ? (JSON.parse(o.arguments) as Record<string, unknown>) : ((o.arguments as Record<string, unknown>) ?? {});
      } catch {
        args = {};
      }
      const call: ToolCall = { callId: String(o.call_id ?? o.id ?? ''), name: String(o.name ?? ''), arguments: args };
      calls.push(call);
      byCall.set(call.callId, call.name);
    } else if (o.type === 'function_call_output') {
      const items = (o.output as unknown[]) ?? [];
      const t = items
        .map((it) => (it && typeof it === 'object' && 'text' in (it as object) ? String((it as { text: unknown }).text ?? '') : typeof it === 'string' ? it : ''))
        .join('');
      const callId = String(o.call_id ?? '');
      results.push({ callId, name: byCall.get(callId) ?? '', text: t });
    } else if (o.type === 'message') {
      const parts = (o.content as { type?: string; text?: string }[]) ?? [];
      text += parts.filter((p) => p.type === 'output_text').map((p) => p.text ?? '').join('');
    }
  }
  if (!text) text = fallbackText;
  const answered = new Set(results.map((r) => r.callId));
  const running = calls.filter((c) => !answered.has(c.callId)).map((c) => c.name);
  return { text, calls, results, running, done };
}

/** A tool argument is only worth showing if it reads like a place/word a human typed — never an
 *  id (`de:03241:…`, `p_100…`), a bare number/coordinate or a timestamp. Keeps the step feed honest
 *  and legible; anything unreadable is dropped rather than shown raw. */
function readableArg(v: unknown): string | undefined {
  if (typeof v !== 'string') return undefined;
  const s = v.trim();
  if (!s || s.length > 60) return undefined;
  if (/^-?\d+([.,]\d+)?$/.test(s)) return undefined;         // bare number / coordinate
  if (/[a-z]+:\d|:\d+:|^[a-z]_\d/i.test(s)) return undefined; // id: de:03241:… / p_100…
  if (/^\d{4}-\d{2}-\d{2}t/i.test(s)) return undefined;       // ISO timestamp
  return s;
}
function firstReadable(args: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const r = readableArg(args[k]);
    if (r) return r;
  }
  return undefined;
}
/** The content-level hint from a call's arguments — real, never invented. `""` when nothing legible. */
function argHint(n: string, args: Record<string, unknown>): string {
  if (/connections/.test(n)) {
    const a = firstReadable(args, ['origin', 'from', 'origin_name', 'start', 'origin_query', 'origin_place']);
    const b = firstReadable(args, ['destination', 'to', 'destination_name', 'ziel', 'destination_query', 'destination_place']);
    if (a && b) return `${a} → ${b}`;
    return b || a || '';
  }
  if (/^(search_place|resolve_location|reverse_geocode)/.test(n)) return firstReadable(args, ['query', 'q', 'name', 'place', 'text']) ?? '';
  if (/departures/.test(n)) return firstReadable(args, ['stop', 'query', 'place', 'name', 'station']) ?? '';
  if (/(tourism_details|poi_details|get_details)/.test(n)) return firstReadable(args, ['name', 'title', 'query']) ?? '';
  if (/search_tourism/.test(n)) return firstReadable(args, ['query', 'q', 'region', 'name', 'place']) ?? '';
  if (/expand_kg_pois/.test(n)) return firstReadable(args, ['query', 'region', 'place', 'name']) ?? '';
  if (/line_course/.test(n)) return [firstReadable(args, ['line', 'line_number', 'lineNumber', 'line_name']), firstReadable(args, ['area', 'region', 'city'])].filter(Boolean).join(' · ');
  if (/nearby/.test(n)) return firstReadable(args, ['node_types', 'query', 'place']) ?? '';
  if (/link_datetime/.test(n)) return firstReadable(args, ['mention', 'text', 'when', 'query', 'value']) ?? '';
  if (/weather|air_quality|tide/.test(n)) return firstReadable(args, ['place', 'name', 'query', 'location']) ?? '';
  return '';
}

/** Human wording for a tool step. With the call's `arguments` it reads like a live summary —
 *  „suche Verbindung Linden-Süd → Steinhuder Meer" — built only from real call arguments. */
export function describeTool(name: string, args?: Record<string, unknown>): string {
  const n = name.replace(/^tmn-(mobility|tourism)-mcp_/, '').replace(/^meta-open-data_/, '');
  const map: Record<string, string> = {
    link_datetime: 'lese den Zeitpunkt',
    resolve_location: 'finde die Haltestelle',
    search_place: 'suche den Ort',
    show_connections: 'suche Verbindung',
    connections: 'suche Verbindung',
    show_departures: 'hole Abfahrten',
    departures: 'hole Abfahrten',
    nearby: 'schaue in der Umgebung',
    search_tourism: 'suche Sehenswertes',
    expand_kg_pois: 'sammle Sehenswürdigkeiten',
    get_tourism_details: 'hole Tour-Infos',
    get_poi_details: 'hole Details',
    disruptions: 'prüfe Störungen',
    get_usage_guide: 'lese die Anleitung',
    reverse_geocode: 'bestimme den Ort',
    get_current_weather: 'schaue nach dem Wetter',
    get_weather_forecast: 'hole die Wettervorhersage',
  };
  const base = map[n] ?? n.replace(/_/g, ' ');
  const hint = args ? argHint(n, args) : '';
  return hint ? `${base}: ${hint}` : base;
}
